/* import __COLOCATED_TEMPLATE__ from './related-conversation-panel.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { ConversationExploreConversation } from 'embercom/components/reporting/automation/ai-insights/conversation-panel';
import { type TaskInstance, type TaskGenerator } from 'ember-concurrency';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { trackedTask } from 'ember-resources/util/ember-concurrency';
import type Conversation from 'embercom/objects/inbox/conversation';
import type InboxApi from 'embercom/services/inbox-api';
import { inject as service } from '@ember/service';
import type ReportingUnderlyingDataService from 'embercom/services/reporting-underlying-data-service';
import type { AttributeMapping } from 'embercom/services/reporting-underlying-data-service';
import { CX_REASON_TO_COLOR } from 'embercom/components/reporting/automation/ai-insights/conversation-stream';
interface Signature {
  Args: {
    conversation: ConversationExploreConversation | undefined;
    isOpen: boolean;
    onClose: () => void;
  };
}

export default class RelatedConversationPanel extends Component<Signature> {
  @service declare inboxApi: InboxApi;
  @service declare reportingUnderlyingDataService: ReportingUnderlyingDataService;
  fetchDataTask = taskFor(this.fetchConversation);
  conversation = trackedTask(this, this.fetchDataTask, () => [
    this.args.conversation?.id,
  ]) as TaskInstance<Conversation | undefined>;

  private cachedConversations: Record<number, Conversation> = {};

  @restartableTask
  *fetchConversation(): TaskGenerator<Conversation | undefined> {
    let conversation: Conversation | undefined =
      this.cachedConversations[this.args.conversation?.id || 0];
    if (!conversation) {
      try {
        conversation = yield this.inboxApi.fetchConversation(this.args.conversation?.id || 0);
        if (conversation) {
          this.cachedConversations[this.args.conversation?.id || 0] = conversation;
        }
      } catch (error) {
        console.error('Error fetching conversation', error);
      }
    }
    return conversation;
  }

  get availableCxAttributes(): AttributeMapping {
    return this.reportingUnderlyingDataService.getAttributeKnownValues(
      'conversation_custom_fields#customer_experience_attributes',
    );
  }

  get cxAttributeOptions() {
    return Object.entries(this.availableCxAttributes).map(([value, text]) => ({
      value,
      text: text as string,
    }));
  }

  get allCxAttributes() {
    let attributes = this.cxAttributeOptions;
    return this.args.conversation?.cxAttributes
      ?.split(',')
      .map((attribute) => attribute.trim())
      .map((attribute) => {
        let attributeId = attributes.find((a) => a.text === attribute)?.value;
        let colorConfig = attributeId
          ? CX_REASON_TO_COLOR[attributeId as keyof typeof CX_REASON_TO_COLOR]
          : undefined;
        return {
          label: attribute,
          color: colorConfig?.color || '', // Fallback to empty string if not found
        };
      });
  }

  get nameToDisplay() {
    let { userName, userPseudonym, userEmail } = this.args.conversation ?? {};
    return userName || userPseudonym || userEmail || '-';
  }

  @action
  handleClose(): void {
    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::AiInsights::OptimizeSuggestions::Suggestions::RelatedConversationPanel': typeof RelatedConversationPanel;
    'reporting/automation/ai-insights/optimize-suggestions/suggestions/related-conversation-panel': typeof RelatedConversationPanel;
  }
}
